<template lang="pug">
transition(name="fade-popup")
  .popup-overlay(
    v-if="visible"
    @click="closePopup" 
    aria-modal="true"
    role="dialog"
  )
    //- 👈 加這行，阻止冒泡
    .popup-content(@click.stop)
      //- 表頭（固定住）
      .popup-header
        h3 {{ headerText }}
      //- 內容：切換時淡入淡出，並自動將卷軸歸零
      transition(name="slide-fade" mode="out-in")
        .contentWrap(:key="activeIndex" ref="contentWrap")
          .contentText
            div(v-html="currentItem.Content" class="rich-html")
      //- 底部操作列：左側「不再顯示」，右側按鈕（僅「下一則」）
      .actions
        label.noshow
          input(type="checkbox" v-model="dontShowAgain")
          span 不再顯示
        div.btns
          button.next-btn(
            v-if="showNextBtn"
            @click="next"
          ) 下一則
          button.close-btn(@click="closePopup") 我知道了
</template>

<script>
import { getAlert } from '@/json/home'

export default {
  name: 'AlertPopup',
  props: {
    // 父層可先傳入暫時標題；之後 API 有 Title 會覆蓋它
    defaultTitle: { type: String, default: '最新消息' },
    // 若要只看特定 Mode，可傳 0 / 1；傳 null 則不過濾
    preferMode: { type: [Number, null], default: 0 },
    // 自動輪播（可選）
    autoplay: { type: Boolean, default: false },
    autoplayInterval: { type: Number, default: 6000 },
    loop: { type: Boolean, default: true },
    // 直接由父層傳進來（建議）
    q: { type: [Number, String], default: null },
    // 若父層沒傳，就用這個當預設
    defaultQ: { type: [Number, String], default: 1 },
  },
  data() {
    return {
      visible: false,
      items: [],       // 所有可顯示的訊息
      activeIndex: 0,  // 目前顯示的索引
      timer: null,
      showNextBtn: false, // ← 由 API 筆數決定是否顯示「下一則」
      dontShowAgain: false, // 綁定 checkbox
    }
  },
  computed: {
    currentItem() {
      return this.items[this.activeIndex] || { Title: '', Content: '' }
    },
    headerText() {
      const t = (this.currentItem.Title || '').trim()
      return t || this.defaultTitle
    },
    // 👉 達門檻才顯示「下一則」
    showNext() {
      return this.items.length >= this.navMinItems
    },
    // 依 q（或 defaultQ）生成 localStorage key
    storageKey() {
      const qVal = (this.q !== null && this.q !== undefined && this.q !== '') ? this.q : this.defaultQ
      return `alertPopup:q=${qVal}`
    }
  },
  watch: {
    // 只要彈窗顯示狀態改變就對外廣播
    visible(val) {
      this.$emit(val ? 'opened' : 'closed')
    },
    // q 改變時重新啟動流程（例如同一顆在不同頁使用）
    q() {
      this.bootstrap()
    }
  },
  mounted() {
    // 延遲一點點，再依 localStorage 決定是否顯示
    setTimeout(() => {
      this.bootstrap()
    }, 300)

    // 鍵盤快捷：← / →
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    this.stopAutoplay()
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    // 啟動：先查 sessionStorage，再決定要不要抓資料/顯示
    async bootstrap() {
      // 若使用者勾過「不再顯示」，就直接不顯示
      const flag = this.readFlag()
      if (flag?.dontShow) {
        this.visible = false
        return
      }
      // 沒勾過 → 正常抓資料並顯示
      await this.fetchPopupContent()
    },
    // --- sessionStorage 讀寫 ---
    readFlag() {
      try {
        const raw = sessionStorage.getItem(this.storageKey)
        return raw ? JSON.parse(raw) : null
      } catch (_) {
        return null
      }
    },
    writeFlag() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          dontShow: true,
          ts: Date.now()
        }))
      } catch (error) {
        console.error('寫入 sessionStorage 失敗：', error)
      }
    },

    async fetchPopupContent() {
      try {

        // console.log('[Alert] q =', this.q)        // ← 確認值
        // const { data } = await getAlert(this.q) // ← 依頁面傳入的 q 取得資料

        const qVal = (this.q !== null && this.q !== undefined && this.q !== '') ? this.q : this.defaultQ
        // console.log('[Alert] q =', qVal)
        const { data } = await getAlert(qVal)

        // ✅ 依「API 原始回傳筆數」判斷是否顯示「下一則」
        this.showNextBtn = Array.isArray(data?.Result) && data.Result.length > 1

        if (data?.Status && Array.isArray(data.Result)) {
          let list = data.Result.filter(x => (x.Content || '').trim())

          // 若要過濾 Mode，打開以下區塊
          // if (this.preferMode !== null) {
          //   const preferred = list.filter(x => x.Mode === this.preferMode)
          //   if (preferred.length) list = preferred
          // }

          this.items = list
          this.activeIndex = 0
          this.visible = this.items.length > 0

          // 啟動自動輪播（可選）
          // if (this.visible && this.autoplay && this.items.length > 1) {
          //   this.startAutoplay()
          // }

          // 重置卷軸
          this.$nextTick(this.resetScrollTop)

          // this.visible = true
        }
      } catch (error) {
        console.error('載入 popup 資料失敗：', error)
        // 失敗時視需求是否仍顯示（這裡關閉）
        this.visible = false
      }
    },
    // --- 導覽控制 ---
    next() {
      if (this.items.length <= 1) return
      if (this.activeIndex < this.items.length - 1) {
        this.activeIndex++
      } else if (this.loop) {
        this.activeIndex = 0
      }
      this.afterSwitch()
    },
    afterSwitch() {
      // 切換時重置卷軸 + 重啟輪播
      this.$nextTick(this.resetScrollTop)
      if (this.autoplay) {
        this.stopAutoplay()
        this.startAutoplay()
      }
    },

    // --- 輪播 ---
    startAutoplay() {
      if (this.timer) return
      this.timer = setInterval(() => {
        this.next()
      }, this.autoplayInterval)
    },
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    // --- 其他 ---
    resetScrollTop() {
      const el = this.$refs.contentWrap
      if (el) el.scrollTop = 0
    },
    onKeydown(e) {
      if (!this.visible) return
      if (e.key === 'ArrowRight') this.next()
      // 你目前沒有 prev()，因此不處理 ArrowLeft
      // if (e.key === 'ArrowLeft') this.prev()
      if (e.key === 'Escape') this.closePopup()
    },

    closePopup() {
      // 勾選「不再顯示」→ 寫入 localStorage
      if (this.dontShowAgain) this.writeFlag()
      this.visible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.popup-overlay {
  position: fixed; inset: 0;
  background-color: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}

.popup-content {
  background:#fff; border-radius:16px;
  max-width: 720px; width: 92%;
  max-height: 90vh; overflow: hidden; // 外層不捲動，由 contentWrap 捲
  box-shadow: 0 4px 20px rgba(0,0,0,.3);
  display: flex; flex-direction: column; position: relative;
}

/* 🔴 上方紅色區塊標題 */
.popup-content h3 {
  background:#0078D0; color:#fff;
  font-size:22px; font-weight:700; text-align:center;
  margin:0; padding:20px 16px;
  border-top-left-radius:16px; border-top-right-radius:16px;
  word-break: break-word;
}

/* 內容可捲動區 */
.contentWrap {
  overflow-y: auto; overflow-x: hidden;
  height: calc(65vh - 160px);
//   max-height: calc(65vh - 160px); // 扣掉表頭 + 導覽 + 按鈕空間
//   min-height: calc(60vh - 160px); // 扣掉表頭 + 導覽 + 按鈕空間
}
.contentText { padding: 20px; }

/* ✅ 中間內容區域（v-html） */
// .popup-content div[v-html] {
.rich-html {
  font-size: 16px; line-height: 1.7; color: #333;
  p { margin: 0 0 1em; }
  a { color: #1a73e8; text-decoration: underline; }
}
/* 導覽列 */
.popup-nav {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; padding: 8px 12px 0;
}
.nav-btn {
  background: transparent; border: 1px solid #ddd; border-radius: 999px;
  width: 36px; height: 36px; cursor: pointer;
  line-height: 1em;
  font-size: 20px;
  padding-bottom: 0.25em;
  &:disabled { opacity: .4; cursor: not-allowed; }
}
.dots { display: flex; gap: 8px; align-items: center; }
.dot {
  width: 8px; height: 8px; border-radius: 999px; background: #cbd5e1;
  cursor: pointer; display: inline-block;
  &.active { background: #0078D0; }
}
.counter { font-size: 14px; color: #666; }

.actions {
  display: flex;
  // justify-content: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
}

.noshow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #475569;
  user-select: none;
}
.btns { display: inline-flex; gap: 8px; }

.next-btn,
.close-btn {
  align-self: flex-end;
  background: #0078D0; color:#fff; border:0;
  padding: 10px 18px; font-size: 16px; font-weight: 700;
  margin: 12px 0px 16px; border-radius: 6px; cursor: pointer;
}

/* 進出場與內容切換動畫 */
.fade-popup-enter-active, .fade-popup-leave-active {
  transition: opacity .35s ease, transform .35s ease;
}
.fade-popup-enter, .fade-popup-leave-to { opacity: 0; }

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}
.slide-fade-enter { opacity: 0; transform: translateY(6px); }
.slide-fade-leave-to { opacity: 0; transform: translateY(-6px); }
</style>